import React, { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import type { ReportingPeriodV01 } from '../../hooks/advisory/advisoryReportingPeriod';
import { contentColumn, eyebrow, focusRing, meta } from '../../styles/visualLanguage';
import { AdvisoryCalloutState } from './components/AdvisoryCalloutState';
import { AdvisoryPeriodConfirmationScreen } from './components/AdvisoryPeriodConfirmationScreen';
import type { AdvisoryDemoEnvironment } from './AdvisoryCompetitorAnalysisDemoPage';

type PreviewState = 'found' | 'defaulted' | 'failed';

const runtimeEnvironment = (): AdvisoryDemoEnvironment => ({
  nodeEnv: process.env.NODE_ENV,
  firebaseProjectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
});

export const isPeriodConfirmationDemoEnabled = (environment: AdvisoryDemoEnvironment): boolean =>
  environment.nodeEnv === 'development' ||
  environment.nodeEnv === 'test' ||
  environment.firebaseProjectId === 'development-477809';

const previewState = (value: string | null): PreviewState =>
  value === 'defaulted' || value === 'failed' ? value : 'found';

const proposalFor = (state: PreviewState): ReportingPeriodV01 =>
  state === 'found'
    ? { year: 2027, quarter: 1, endQuarter: 3, yearType: 'fiscal', fiscalStartMonth: 4 }
    : {
        year: new Date().getFullYear(),
        quarter: null,
        endQuarter: null,
        yearType: 'calendar',
        fiscalStartMonth: 1,
      };

export function AdvisoryPeriodConfirmationDemoPage({
  environment = runtimeEnvironment(),
}: {
  environment?: AdvisoryDemoEnvironment;
}) {
  const { t } = useUntypedTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const state = previewState(searchParams.get('state'));
  const [submitted, setSubmitted] = useState<ReportingPeriodV01 | null>(null);
  const proposal = proposalFor(state);

  if (!isPeriodConfirmationDemoEnabled(environment)) {
    return (
      <AdvisoryCalloutState
        tone="info"
        headingKey="advisory:callout.absent.heading"
        bodyKey="advisory:errors.notFound"
      />
    );
  }

  const selectState = (next: PreviewState) => {
    setSubmitted(null);
    setSearchParams({ state: next }, { replace: true });
  };

  return (
    <Box data-testid="advisory-period-preview">
      <Box sx={theme => ({ backgroundColor: theme.palette.background.default, paddingX: 2.5 })}>
        <Box
          sx={[
            contentColumn,
            theme => ({
              paddingY: 1.5,
              borderBottom: `1px solid ${theme.visualPrimitives.border}`,
            }),
          ]}
        >
          <Box data-testid="advisory-period-preview-label" role="status" aria-live="polite">
            <Typography id="advisory-period-preview-heading" sx={[eyebrow]}>
              {t('advisory:period.previewBadge')}
            </Typography>
            {submitted ? (
              <Typography sx={[meta, { marginTop: 0.5 }]}>
                {t('advisory:period.previewCaptured', { year: submitted.year })}
              </Typography>
            ) : null}
          </Box>
          <Stack
            direction="row"
            spacing={{ xs: 1.5, sm: 2.5 }}
            useFlexGap
            flexWrap="wrap"
            role="group"
            aria-labelledby="advisory-period-preview-heading"
            sx={{ marginTop: 0.5 }}
          >
            {(['found', 'defaulted', 'failed'] as const).map(item => (
              <Button
                key={item}
                size="small"
                variant="text"
                disableRipple
                aria-pressed={state === item}
                onClick={() => selectState(item)}
                sx={[
                  meta,
                  focusRing,
                  theme => ({
                    minWidth: 0,
                    paddingX: 0,
                    paddingY: 0.5,
                    borderRadius: 0,
                    textTransform: 'none',
                    color: state === item ? theme.colors.inkPrimary : theme.colors.inkTertiary,
                    textDecoration: state === item ? 'underline' : 'none',
                    textUnderlineOffset: '0.25em',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: theme.colors.inkPrimary,
                      textDecoration: 'underline',
                    },
                  }),
                ]}
              >
                {t(`advisory:period.preview${item[0].toUpperCase()}${item.slice(1)}`)}
              </Button>
            ))}
          </Stack>
        </Box>
      </Box>
      <AdvisoryPeriodConfirmationScreen
        key={state}
        period={submitted ?? proposal}
        defaulted={state !== 'found'}
        extractionFailed={state === 'failed'}
        acceptStoredProposal={false}
        onConfirm={value => setSubmitted(value ?? proposal)}
      />
    </Box>
  );
}

export default AdvisoryPeriodConfirmationDemoPage;
